import dayjs from 'dayjs';
import {
  OutboundFulfilledExternallySKU,
  OutboundProcess,
} from '@features/supplyChain/types';
import { SkuInfoLink } from '@tailwindComponents/SkuInfoModal';

export type FulfilledExternallyDetails = NonNullable<
  OutboundProcess['fulfilledExternally']
>;

export const fulfilmentDate = (
  details: FulfilledExternallyDetails
): string | undefined => details.actualFulfilmentDate ?? details.markedAt;

const isUnrecorded = (value: number | null | undefined): boolean =>
  value === null || value === undefined;

const shippedLine = (sku: OutboundFulfilledExternallySKU): string =>
  `Shipped ${sku.wholeUnitsShipped ?? 0} whole · ${
    sku.looseProductsShipped ?? 0
  } loose`;

const arrivedLine = (sku: OutboundFulfilledExternallySKU): string =>
  isUnrecorded(sku.wholeUnitsArrived) && isUnrecorded(sku.looseProductsArrived)
    ? 'Arrival not recorded'
    : `Arrived ${sku.wholeUnitsArrived ?? 0} whole · ${
        sku.looseProductsArrived ?? 0
      } loose`;

export function FulfilledExternallySkuList({
  skus,
}: {
  skus: OutboundFulfilledExternallySKU[];
}) {
  return (
    <ul className='divide-y divide-gray-100 rounded-lg border border-gray-100'>
      {skus.map((sku) => (
        <li key={sku.skuId} className='px-3 py-2 text-sm'>
          <p className='text-gray-900'>
            <SkuInfoLink inventorySkuId={sku.skuId}>
              {sku.name || sku.skuIdentifier || sku.skuId}
            </SkuInfoLink>
          </p>
          <p className='text-xs text-gray-500'>{shippedLine(sku)}</p>
          <p className='text-xs text-gray-500'>{arrivedLine(sku)}</p>
        </li>
      ))}
    </ul>
  );
}

export function FulfilledExternallySummary({
  fulfilledExternally,
}: {
  fulfilledExternally: FulfilledExternallyDetails;
}) {
  return (
    <div className='space-y-4'>
      <div className='grid grid-cols-2 gap-x-4 gap-y-3'>
        <div>
          <p className='text-sm font-semibold text-gray-900'>
            Shipped outside Flox
          </p>
          <p className='text-xs text-gray-400'>Fulfilment</p>
        </div>
        <div>
          <p className='text-sm font-semibold text-gray-900'>
            {fulfilmentDate(fulfilledExternally)
              ? dayjs(fulfilmentDate(fulfilledExternally)).format('DD/MM/YY')
              : '—'}
          </p>
          <p className='text-xs text-gray-400'>Fulfilment date</p>
        </div>
        {fulfilledExternally.comment && (
          <div className='col-span-2 pt-2'>
            <p className='text-sm text-gray-700'>
              {fulfilledExternally.comment}
            </p>
            <p className='text-xs text-gray-400'>Comment</p>
          </div>
        )}
      </div>
      {fulfilledExternally.skus?.length ? (
        <FulfilledExternallySkuList skus={fulfilledExternally.skus} />
      ) : null}
    </div>
  );
}
